import { useContext, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import ErrorBox from 'components/ErrorBox'
import ExtraLargeHeader from 'components/ExtraLargeHeader'
import TextInput from 'components/GlobalStyles/Inputs/TextInput'
import RoundedButton from 'components/RoundedButton'
import Spacer from 'components/Spacer'

import { requestResetPassword } from 'services/auth'

import validateEmail from 'utils/validateEmail'

import * as E from 'types/enums'

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  padding: 0 10px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 0 15px;
  }
`

const FormContainer = styled.div`
  flex-basis: 500px;
  flex-grow: 0;
`

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const StyledText = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 19px;
  color: ${props => props.theme.colors.black};
  text-align: center;
`

const RequestResetPassword = () => {
  const theme = useContext(ThemeContext)

  // error state
  const [requestError, setRequestError] = useState('')
  const resetRequestError = () => setRequestError('')

  // sumbission state
  const [submitted, setSubmitted] = useState(false)

  // request password change email state
  const [requestEmail, setRequestEmail] = useState('')
  const handleRequestEmailChange = (_: string, value: string) => setRequestEmail(value)

  const handleRequestEmailChangeImmediate = () => {
    if (submitted) setSubmitted(false)

    if (requestError) resetRequestError()
  }

  const handleRequestEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedEmail = requestEmail.trim()
    if (requestError) resetRequestError()

    if (!trimmedEmail) return setRequestError('Email is required.')

    if (!validateEmail(trimmedEmail)) return setRequestError('Email is invalid.')

    const result = await requestResetPassword(trimmedEmail)

    if (result?.message === 'success') setSubmitted(true)
    else setRequestError(result.message)
  }

  return (
    <Container>
      <FormContainer>
        <ExtraLargeHeader title="Request Password Reset" />
        <Spacer size={50} />
        <StyledForm onSubmit={handleRequestEmailSubmit}>
          {submitted && !requestError
            ? (
              <>
                <Spacer size={10} />
                <StyledText>We sent a reset link to your email. Please check your inbox.</StyledText>
              </>
            )
            : (
              <>
                <TextInput
                  name="email"
                  placeholder="Email"
                  placeholderTextColor={theme.colors.black}
                  value={requestEmail}
                  delay={250}
                  submissionError={!!requestError}
                  onChange={handleRequestEmailChange}
                  onChangeImmediate={handleRequestEmailChangeImmediate}
                />
                <Spacer size={10} />
                <RoundedButton
                  type={E.ButtonType.submit}
                  text="Send Password Reset"
                  background={theme.colors.purple}
                  color={theme.colors.white}
                  radius={20}
                />
              </>
            )}
          {requestError && <Spacer size={10} />}
          {requestError && <ErrorBox error={requestError} />}
        </StyledForm>
      </FormContainer>
    </Container>
  )
}

export default RequestResetPassword
